import { useEffect, useRef, useState } from "react";
import { API, type TimelineItem } from "../api";

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export function Chat({ savId, onSAVUpdated }: { savId: string; onSAVUpdated: () => void }) {
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  async function refresh() {
    try {
      const items = await API.getTimeline(savId);
      setTimeline(items);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 5000);
    return () => clearInterval(iv);
  }, [savId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [timeline, typing]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    const toSend = message.trim();
    setMessage("");
    setSending(true);
    setTyping(true);
    setError(null);
    try {
      await API.sendMessage(savId, toSend);
      await refresh();
      onSAVUpdated();
    } catch (e) {
      setError((e as Error).message);
      setMessage(toSend);
    } finally {
      setSending(false);
      setTyping(false);
    }
  }

  return (
    <>
      <div className="xs-chat-area">
        {timeline.length === 0 && (
          <div className="xs-chat-divider">Démarrage de la conversation</div>
        )}
        {timeline.map((e) => {
          if (e.kind === "status_change") {
            return (
              <div key={e.id} className="xs-chat-divider">
                {e.content}
              </div>
            );
          }
          if (e.kind === "system") return null;
          const isMe = e.kind === "client_in";
          return (
            <div
              key={e.id}
              className={isMe ? "xs-bubble xs-bubble-me" : "xs-bubble xs-bubble-them"}
            >
              <div>{e.content || ""}</div>
              <span className="xs-bubble-time">{formatTime(e.created_at)}</span>
            </div>
          );
        })}
        {typing && (
          <div
            className="xs-bubble xs-bubble-them"
            style={{ fontStyle: "italic", color: "#64748b" }}
          >
            Xsavier écrit…
          </div>
        )}
        <div ref={endRef} />
      </div>

      {error && <div className="xs-alert xs-alert-error" style={{ margin: 8 }}>{error}</div>}

      <form onSubmit={send} className="xs-chat-footer">
        <input
          className="xs-input"
          placeholder="Votre message…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={sending}
          autoFocus
        />
        <button
          type="submit"
          className="xs-btn xs-btn-primary"
          disabled={sending || !message.trim()}
        >
          {sending ? "…" : "Envoyer"}
        </button>
      </form>
    </>
  );
}
